/* Figma-derived header component adapted to project Tailwind setup */
import React from "react";
import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { initAmplify } from "../../../lib/amplify";
import { signOutWithMediaCookieRevoke } from "../../../lib/logout";

import iconButtonSvg from "../../../assets/ProfileIcon/IconButton_Manager.svg";
import brandLogoSvg from "../../../assets/Icon/Logo.svg";
import { Button } from "../Button/Button";

export function Header() {
  initAmplify();
  const [displayName, setDisplayName] = React.useState<string>("未ログイン");
  const [isAuthed, setIsAuthed] = React.useState<boolean>(false);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const hasIdToken = Boolean(session.tokens?.idToken);
        if (mounted) setIsAuthed(hasIdToken);
        if (!hasIdToken) {
          if (mounted) setDisplayName("未ログイン");
          return;
        }
        const claims: any = session.tokens?.idToken?.payload ?? {};
        const username =
          claims["cognito:username"] || claims.preferred_username;
        const name =
          username ||
          claims.name ||
          (claims.given_name && claims.family_name
            ? `${claims.family_name} ${claims.given_name}`
            : claims.email || "ログイン済み");
        if (mounted) setDisplayName(name);
      } catch {
        if (mounted) {
          setDisplayName("未ログイン");
          setIsAuthed(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const onLogin = async () => {
    try {
      await signInWithRedirect({ options: { lang: "ja" } });
    } catch {
      alert("ログインに失敗しました。");
    }
  };

  const onLogout = async () => {
    try {
      await signOutWithMediaCookieRevoke();
    } catch {
      alert("ログアウトに失敗しました。");
    }
  };

  return (
    <header
      className="bg-brandBlue w-full h-header flex items-center px-s-600 py-s-300"
      data-name="Header"
      data-node-id="979:25874"
    >
      {/* Left block */}
      <div
        className="flex items-center gap-s-100 flex-1 min-w-px min-h-px"
        data-node-id="979:25810"
      >
        <div
          className="w-[40px] h-[27px] relative shrink-0"
          data-name="Figma"
          data-node-id="979:25813"
        >
          <img
            alt="logo"
            className="block max-w-none w-full h-full"
            src={brandLogoSvg}
          />
        </div>
        <p
          className="text-brandOnBrandText text-lg font-medium"
          data-node-id="979:25815"
        >
          SASオンラインクリニック
        </p>
        <div className="px-[10px]" data-node-id="979:25868">
          <p
            className="text-brandOnBrandText text-base tracking-tiny"
            data-node-id="979:25869"
          >
            NTT西日本
          </p>
        </div>
      </div>

      {/* Right block */}
      <div
        className="flex items-center justify-end gap-s-250 h-[39px]"
        data-node-id="979:25817"
      >
        <div
          className="rounded-pill size-[33px] overflow-clip flex items-center justify-center"
          data-name="Icon Button"
          data-node-id="979:25818"
        >
          <img
            alt="avatar"
            className="w-full h-full object-cover"
            src={iconButtonSvg}
          />
        </div>
        <p className="text-brandOnBrandText text-sm">{displayName}</p>
        {isAuthed ? (
          <Button
            label="ログアウト"
            variant="solid"
            tone="brand"
            onClick={onLogout}
            dataName="LogoutButton"
            dataNodeId="ui:logout"
          />
        ) : (
          <Button
            label="ログイン"
            variant="solid"
            tone="brand"
            onClick={onLogin}
            dataName="LoginButton"
            dataNodeId="ui:login"
          />
        )}
      </div>
    </header>
  );
}

export default Header;
